import { useState } from "react";
import { toast } from "sonner";
import { api, BUSINESS, getUtm } from "@/lib/sleek";
import { fireConversion } from "@/lib/gtag";
import { Phone, Mail, MapPin } from "lucide-react";

const PROJECTS = ["Kitchen", "Bathroom", "Whole-Home", "Showroom Visit", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "Kitchen", budget: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, ...getUtm(), source: "contact" });
      fireConversion("lead");
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", project_type: "Kitchen", budget: "", message: "" });
    } catch (e) {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl font-light tracking-tight mt-4 leading-[1.05]">Let's start a conversation.</h1>
          <p className="text-zinc-600 mt-6 leading-relaxed">
            Tell us what you're thinking — kitchen layout, dream bath, whole-home reset or whatever
            else is on your mind. We'll respond to your request quickly and would love to discuss
            your needs and how we can partner with you to complete them.
          </p>
          <div className="mt-10 space-y-4 text-zinc-700">
            <div className="flex items-start gap-3"><MapPin size={16} className="mt-1" /> {BUSINESS.address}</div>
            <div className="flex items-start gap-3"><Phone size={16} className="mt-1" /> <a href={BUSINESS.phoneHref} className="hover:underline">{BUSINESS.phone}</a></div>
            <div className="flex items-start gap-3"><Mail size={16} className="mt-1" /> <a href={BUSINESS.emailHref} className="hover:underline break-all">{BUSINESS.email}</a></div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contact-form">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">Project</label>
            <select
              value={form.project_type}
              onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              className="w-full border-b border-zinc-300 bg-transparent py-2 focus:outline-none focus:border-zinc-950"
              data-testid="contact-project"
            >
              {PROJECTS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <Field label="Budget range (optional)" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} testid="contact-budget" className="md:col-span-2" />
          <div className="md:col-span-2">
            <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">Message</label>
            <textarea
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full border-b border-zinc-300 bg-transparent py-2 focus:outline-none focus:border-zinc-950 resize-none"
              data-testid="contact-message"
            />
          </div>
          <button disabled={submitting} className="md:col-span-2 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition disabled:opacity-50" data-testid="contact-submit">
            {submitting ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-zinc-300 bg-transparent py-2 focus:outline-none focus:border-zinc-950"
        data-testid={testid}
      />
    </div>
  );
}
